<template>
<div class="form-wrapper">
    <div class="row" v-if="!confirmed">
        <div class="col-md-6 left">
            <logo-header :title="'Profile Details'" :cardIndex="3"></logo-header>
            <p class="description">
                We capture only the minimum data required to positively identify your company
                and create a secure user profile for accessing your company's vulnerability data.
            </p>
        </div>
        <div class="col-md-6 right">
            <h2>Profile Details</h2>
            <div class="form-group" :class="{'has-error': errors.first_name}">
                <label>First Name</label>
                <input type="text" class="form-control"
                       name="first_name" v-model="first_name" @blur="validate($event, ['req'])">
                <p v-if="errors.first_name" class="error-text">
                    {{ errors.first_name }}
                </p>
            </div>
            <div class="form-group" :class="{'has-error': errors.last_name}">
                <label>Last Name</label>
                <input type="text" class="form-control"
                       name="last_name" v-model="last_name" @blur="validate($event, ['req'])">
                <p v-if="errors.last_name" class="error-text">
                    {{ errors.last_name }}
                </p>
            </div>
            <div class="form-group" :class="{'has-error': errors.job_title}">
                <label>Job Title</label>
                <input type="text" class="form-control"
                       name="job_title" v-model="job_title" @blur="validate($event, ['req'])">
                <p v-if="errors.job_title" class="error-text">
                    {{ errors.job_title }}
                </p>
            </div>
            <div class="form-group" :class="{'has-error': errors.company_name}">
                <label>Company Name</label>
                <input type="text" class="form-control"
                       name="company_name" v-model="company_name" @blur="validate($event, ['req'])">
                <p v-if="errors.company_name" class="error-text">
                    {{ errors.company_name }}
                </p>
            </div>
            <div class="form-group" :class="{'has-error': errors.industry}">
                <label>Industry</label>
                <select v-model="industry" @blur="validate($event, ['req'])">
                    <option value="" disabled selected></option>
                    <option v-for="i in industries" :value="i" :key="i">{{ i }}</option>
                </select>
                <p v-if="errors.industry" class="error-text">
                    {{ errors.industry }}
                </p>
            </div>
            <div class="form-group" :class="{'has-error': errors.phone_number}">
                <label>Phone Number</label>
                <input type="phone" class="form-control"
                       name="phone_number" v-model="phone_number_input" ref="phone" @keyup="validate($event, ['req', 'phone'])" @input="validate($event, ['req', 'phone'])">
                <p v-if="errors.phone_number" class="error-text">
                    {{ errors.phone_number }}
                </p>
            </div>
            <div class="submit-data">
                <button @click="sendDetails" type="submit" class="btn" :class="buttonState" v-if="!isSubmitting">
                    Create Profile
                </button>
                <div class="spinner" v-else><i class="fa fa-spinner fa-pulse"></i></div>
            </div>
        </div>
    </div>
    <div class="confirmation" v-else>
        <logo-header :title="'Start Free Scan'" :cardIndex="4"></logo-header>
        <div class="body">
            <p class="description">
                <strong>Welcome to {{getBrandingVar('app_name')}}!</strong><br><br>
                Your scan has been queued and will start shortly. You will receive an email
                when your scan is finished. Thank you.
            </p>
            <a class="btn confirm-btn" href="/report?scan_id=c10ac53-186b-463b-a9f0-cd91ab16db67">
                View Your Scan Progress
            </a>
        </div>
    </div>
</div>
</template>

<script>
/* eslint-disable import/extensions */
import intlTelInput from 'intl-tel-input';
import LogoHeader from './logo-header.vue';
import Validate from '~/mixins/validation.js';
import { newIndustries } from '~/assets/constants/industries.js';

export default {
    components: { LogoHeader },
    mixins: [Validate],
    data() {
        return {
            first_name: '',
            last_name: '',
            company_name: '',
            job_title: '',
            app_name: '',
            iti_phone: '',
            dial_code: 1,
            phone_number_input: '',
            modalState: {
                terms: false,
            },
            confirmed: false,
            isSubmitting: false,
            industries: newIndustries,
            industry: '',
        };
    },
    computed: {
        proceed_report_button_class() {
            if (this.getBranding() === 'autobahn') {
                return 'btn-complementary';
            }
            return this.getBrandingVar('css_class_primary_button');
        },
        logo() {
            const logo = this.getBrandingVar('app_logo');
            return `background-image: url('${logo}');`;
        },
        phone_number() {
            return `+${this.dial_code}${this.phone_number_input}`;
        },
        valid() {
            let state = this.first_name && this.last_name && this.company_name && this.industry && this.phone_number && this.job_title;
            state = state && this.validate('first_name', ['req']);
            state = state && this.validate('last_name', ['req']);
            state = state && this.validate('company_name', ['req']);
            state = state && this.validate('job_title', ['req']);
            state = state && this.validate('industry', ['req']);
            state = state && this.validate('phone_number', ['req', 'phone']);
            return state;
        },
        buttonState() {
            return this.valid ? 'confirm-btn' : 'btn-unclickable';
        },
    },
    mounted() {
        const self = this;
        this.app_name = this.getBrandingVar('app_name');
        if (typeof gtag !== 'undefined') {
            // eslint-disable-next-line no-undef
            gtag('event', 'soft_sign_up', {
                page_location: `${document.location.protocol}//${document.location.host}${document.location.pathname}`,
            });
        }
        this.iti_phone = intlTelInput(this.$refs.phoneNumber.$refs.inputSet, {
            separateDialCode: true,
            initialCountry: 'auto',
            geoIpLookup: (callback) => {
                this.doBackendRequest(
                    'GET',
                    '/api/get_user_location_by_ip',
                    (res) => {
                        if (res.response === 'OK') {
                            callback(res.country_code);
                        }
                    },
                    undefined,
                    () => {
                        callback('us');
                    },
                );
            },
        });
        this.$refs.phone.addEventListener('countrychange', () => {
            self.dial_code = self.iti_phone.getSelectedCountryData().dialCode;
            self.validate('phone_number', ['req', 'phone']);
        });
    },
    methods: {
        sendDetails(e) {
            e.preventDefault();
            const self = this;

            this.validate('first_name', ['req']);
            this.validate('last_name', ['req']);
            this.validate('company_name', ['req']);
            this.validate('job_title', ['req']);
            this.validate('industry', ['req']);
            this.validate('phone_number', ['req', 'phone']);

            if (this.valid) {
                this.isSubmitting = true;
                this.doBackendRequest('POST', '/api/signup', () => {
                    if (typeof gtag !== 'undefined') {
                        // eslint-disable-next-line no-undef
                        gtag('event', 'sign_up', {
                            transport_type: 'beacon',
                            page_location: `${document.location.protocol}//${document.location.host}${document.location.pathname}`,
                        });
                        // eslint-disable-next-line no-undef
                        gtag('event', 'conversion', { send_to: 'AW-753836733/gM-cCIP8-7EBEL3FuucC' });
                    }
                    localStorage.setItem('FIRST_REPORT', true);
                    self.$emit('details_sent');
                    self.confirmed = true;
                    self.isSubmitting = false;
                },
                JSON.stringify({
                    first_name: this.first_name,
                    last_name: this.last_name,
                    company_name: this.company_name,
                    industry: this.industry,
                    job_title: this.job_title,
                    phone_number: this.phone_number,
                    tos_consent: true,
                }),
                (response) => {
                    self.isSubmitting = false;
                    this.$emit('updateModal', {
                        key: 'error',
                        state: true,
                        message: response.error,
                    });
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.left {
    padding: 0;
    padding-right: 1.25rem;
    h2 {
        font-weight: 600;
    }
    .description {
        padding: 1.5rem 1.25rem;
    }
}
.right {
    padding: 3rem 1.25rem !important;
}
.confirmation {
    background: white;
    box-shadow: -3px -3px 3px rgba(0, 0, 0, 0.13), 3px 3px 3px rgba(0, 0, 0, 0.13);
    .body {
        padding: 1.5rem 1.25rem 3rem;
        .description {
            font-size: 1rem;
            margin-bottom: 2rem;
        }
    }
}
.form-group {
    margin-bottom: 0.5rem;
}
.submit-data {
    margin-top: 2rem;
}
</style>
